import React, { useCallback, useContext } from 'react';

import { OneOnOneInstanceContext as DeerOneOnOneInstanceContext } from 'Deer/pages/MyData/OneOnOne/LegacyInstancePage/LegacyInstancePage';
import CustomMenu from 'components/CustomMenu';
import { useMatchedRoute } from 'components/hooks/useMatchedRoute';
import { useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { getLegacyOneOnOneInstancePagePath } from 'screens/oneOnOneRabbit/helpers';
import { StyledBox, StyledTooltip } from 'styles/common';
import { checkNewNavEnabled } from 'utils/helpers/featureFlags';

import { ONE_ON_ONE_STATUSES } from 'constants/ProjectConstants';
import { noOp } from 'constants/helper';
import { ONE_ON_ONE_EVENTS_OLD, sendOneOnOneEvent } from 'constants/mixpanelHelper';
import { routes } from 'constants/routes';
import theme, { primaryColor } from 'constants/theme';

import { showErrorSnackbar } from 'services/snackbar';
import { acknowledgeOneOnOne } from 'services/task';

import DateComp from 'uiToolkit/DateComp';
import { Typography13, Typography14 } from 'uiToolkit/Typography';

import { StyledChevronDownIcon } from './Header';
import { OneOnOneInstanceContext } from './InstancePageContext';

export const InstanceList = ({ currentInstanceId, isManager, isActiveTask, isDisabled = false }) => {
  const contextToUse = checkNewNavEnabled() ? DeerOneOnOneInstanceContext : OneOnOneInstanceContext;

  const history = useHistory();
  const dispatch = useDispatch();

  const { pageName } = useMatchedRoute();

  const {
    state: { detailedTimeLineData },
  } = useContext(contextToUse);

  const updateStatus = useCallback(
    (instanceId, status) => {
      if (isActiveTask) {
        return acknowledgeOneOnOne({ checkInId: instanceId, status }).catch((err) =>
          dispatch(showErrorSnackbar(err?.message || 'Status updation failed.'))
        );
      }
      return Promise.resolve();
    },
    [isActiveTask]
  );

  function goToOneOnOneInstancePage(history, instanceId) {
    const route = routes.oneOnOne.instance.replace(':instanceId', instanceId);
    history.replace(route);
  }

  const handleActionButtonClick = useCallback(
    ({ instanceId, currentStatus }) => {
      let eventName = null;
      switch (currentStatus) {
        case ONE_ON_ONE_STATUSES.NOT_STARTED:
          eventName = ONE_ON_ONE_EVENTS_OLD.startedOneOnOne;
          updateStatus(instanceId, ONE_ON_ONE_STATUSES.ACTIVE).then(() => {
            document.body.click();
            if (isDisabled) {
              const legacyRedirectRoute = getLegacyOneOnOneInstancePagePath({
                legacyInstanceId: instanceId,
              });
              history.push(legacyRedirectRoute);
            } else {
              goToOneOnOneInstancePage(history, instanceId);
            }
          });
          break;

        case ONE_ON_ONE_STATUSES.ACTIVE:
          document.body.click();
          if (isDisabled) {
            const legacyRedirectRoute = getLegacyOneOnOneInstancePagePath({
              legacyInstanceId: instanceId,
            });
            history.push(legacyRedirectRoute);
          } else {
            goToOneOnOneInstancePage(history, instanceId);
          }
          break;

        case ONE_ON_ONE_STATUSES.ACKNOWLEDGED:
          document.body.click();
          if (isDisabled) {
            const legacyRedirectRoute = getLegacyOneOnOneInstancePagePath({
              legacyInstanceId: instanceId,
            });
            history.push(legacyRedirectRoute);
          } else {
            goToOneOnOneInstancePage(history, instanceId);
          }
          break;

        default:
          break;
      }

      // send event to for tracking.
      sendOneOnOneEvent({
        eventName,
        page: {
          pageName,
          flow: 'Activity',
        },
      });
    },
    [isManager, history, isDisabled]
  );

  const getButtonText = useCallback(
    (status) => {
      switch (status) {
        case ONE_ON_ONE_STATUSES.NOT_STARTED:
        case ONE_ON_ONE_STATUSES.DISABLED:
          return 'Not Started';
        case ONE_ON_ONE_STATUSES.ACTIVE:
          return 'Active';
        case ONE_ON_ONE_STATUSES.ACKNOWLEDGED:
          return 'Completed';

        case ONE_ON_ONE_STATUSES.CANCELLED:
          return 'Cancelled';
        default:
          return '';
      }
    },
    [isManager]
  );

  const onClickAway = () => {
    document.body.click();
  };

  const loading = detailedTimeLineData.length <= 1;

  if (!detailedTimeLineData || !Array.isArray(detailedTimeLineData)) return null;
  return (
    <>
      {
        <StyledBox display="flex">
          {loading || isDisabled ? (
            <StyledBox display="flex" justifyContent="center" alignContent="center">
              <StyledTooltip title="">
                <StyledChevronDownIcon
                  size="fixed10px"
                  containerSize="fixed24px"
                  circularOutline
                  color={theme.palette.grey.main}
                />
              </StyledTooltip>
            </StyledBox>
          ) : (
            <CustomMenu
              icon={
                <StyledBox display="flex" justifyContent="center" alignContent="center" style={{ cursor: 'pointer' }}>
                  <StyledTooltip title="All 1:1s">
                    <StyledChevronDownIcon
                      size="fixed10px"
                      containerSize="fixed24px"
                      circularOutline
                      color={theme.palette.primary.main}
                    />
                  </StyledTooltip>
                </StyledBox>
              }
              paperStyles={{
                minWidth: '15rem',
                padding: '1.5rem',
                paddingTop: '1rem',
                maxHeight: '50vh',
                overflow: 'auto',
              }}
              menuId="instance-list-menu"
              onClickAway={onClickAway}
            >
              <StyledBox display="grid" gridTemplateColumns="auto auto" gridColumnGap="1rem">
                <StyledBox>
                  <Typography14 color={theme.palette.grey.dark}>Past 1:1</Typography14>
                </StyledBox>
                <StyledBox>
                  <Typography14 color={theme.palette.grey.dark} textalign="end">
                    Status
                  </Typography14>
                </StyledBox>
              </StyledBox>

              {detailedTimeLineData.map((timeLineObj) => {
                const { startTime: coloredStartDate, dueTime: coloredDueDate, status, id } = timeLineObj;

                // Exclude "ACTIVE" and "NOT_STARTED" statuses
                if (status === ONE_ON_ONE_STATUSES.ACTIVE || status === ONE_ON_ONE_STATUSES.NOT_STARTED) {
                  return null; // Skip rendering for these statuses
                }

                const isCrossed =
                  status === ONE_ON_ONE_STATUSES.CANCELLED || status === ONE_ON_ONE_STATUSES.ACKNOWLEDGED;
                return (
                  <StyledBox
                    style={{ cursor: 'pointer' }}
                    key={id}
                    display="grid"
                    gridTemplateColumns="auto auto"
                    gridColumnGap="1rem"
                    topGutter={3}
                    alignItems="center"
                    onClick={() => handleActionButtonClick({ instanceId: id, currentStatus: status })}
                  >
                    <StyledBox item xs={4}>
                      <DateComp
                        startDateTime={coloredStartDate.timeStamp ? new Date(coloredStartDate.timeStamp) : null}
                        endDateTime={coloredDueDate.timeStamp ? new Date(coloredDueDate.timeStamp) : null}
                        color={isCrossed ? theme.palette.grey.main : primaryColor}
                        tooltip=""
                        isCrossed={isCrossed}
                        onDateTimeChange={noOp}
                        editable={false}
                      />
                    </StyledBox>
                    <StyledBox>
                      <Typography13 color={theme.palette.grey.main} textalign="end">
                        {getButtonText(status)}
                      </Typography13>
                    </StyledBox>
                  </StyledBox>
                );
              })}
            </CustomMenu>
          )}
        </StyledBox>
      }
    </>
  );
};
